import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import CTASection from "@/components/site/CTASection";
import { usePageMeta } from "@/components/site/SEO";
import { AI_PERSPECTIVE, SAFETY_COPY } from "@/content/site";
import { ASSETS } from "@/content/assets";

const CHAPTERS = [
  {
    number: "01",
    title: "Place is the teacher",
    text: "At Wandering Glider Experiences, we believe that learning is most powerful when it is rooted in place. The destination itself becomes the classroom: whether it's a dense rainforest, a bustling coastal village, or a remote mountain trail. Our programmes are designed to immerse learners in their surroundings, encouraging direct engagement with the landscapes, cultures, and communities they explore.",
  },
  {
    number: "02",
    title: "Slow, on purpose",
    text: "Our approach values slow travel: moving intentionally, taking time to observe, ask questions, and develop a nuanced understanding of the world. Learning is not about rushing from one sight to another but about being present, building relationships, and allowing curiosity to lead the way.",
  },
  {
    number: "03",
    title: "Hands, heart, story",
    text: "We embrace place-based learning, where knowledge is drawn from the land, its history, and the people who call it home. Through hands-on experiences, storytelling, and reflection, we help participants connect deeply with the environments they travel through.",
  },
  {
    number: "04",
    title: "Across disciplines",
    text: "Real places refuse to sit inside subject boundaries. A tide pool holds ecology, livelihood and story at once; a forest carries science, history and community together. By collaborating with experts across disciplines, we let each place teach in its own many-sided way.",
  },
];

export default function Approach() {
  usePageMeta(
    "Our Approach",
    "Place-based learning, slow travel, hands-on experience, storytelling and reflection: how Wandering Glider Experiences designs learning journeys for schools.",
  );

  return (
    <>
      <PageHero
        eyebrow="Our Approach"
        title="Learning rooted in place"
        intro="We design journeys in which the destination does the teaching: slowly, hands-on and across disciplines, with room for curiosity to lead."
      />

      <section className="container-x pb-20 sm:pb-28" data-testid="approach-chapters">
        <Reveal>
          <img
            src={ASSETS.approachBanner.src}
            alt={ASSETS.approachBanner.alt}
            loading="lazy"
            className="aspect-[16/7] w-full rounded-2xl object-cover"
          />
        </Reveal>
        <div className="mt-6">
          {CHAPTERS.map((chapter, i) => (
            <Reveal key={chapter.number} delay={0.05}>
              <div className="grid gap-6 border-t border-line py-12 sm:py-16 md:grid-cols-[240px_1fr] lg:grid-cols-[320px_1fr]">
                <div className="flex items-start gap-5">
                  <span className="font-serif text-5xl font-medium text-brass" aria-hidden="true">
                    {chapter.number}
                  </span>
                  <h2 className="text-2xl font-medium tracking-tight sm:text-3xl">{chapter.title}</h2>
                </div>
                <p className="max-w-2xl text-lg leading-relaxed text-ink/85">{chapter.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <figure className="mt-16 max-w-3xl border-l-2 border-brass bg-white p-8 sm:p-10" data-testid="ai-perspective">
            <figcaption className="eyebrow">Our perspective</figcaption>
            <blockquote className="mt-4 font-serif text-xl italic leading-relaxed text-ink/90 sm:text-2xl">
              {AI_PERSPECTIVE}
            </blockquote>
          </figure>
        </Reveal>
      </section>

      <section id="safety" className="border-t border-line bg-white" data-testid="approach-safety">
        <div className="container-x grid items-stretch gap-12 py-20 sm:py-28 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <p className="mb-5 text-sm font-semibold uppercase tracking-[0.22em] text-[#7C6E33]">
              {SAFETY_COPY.heading}
            </p>
            <h2 className="max-w-2xl text-3xl font-medium leading-tight tracking-tight sm:text-4xl">
              Challenged, supported, safe
            </h2>
            <div className="mt-8 max-w-2xl space-y-6">
              {SAFETY_COPY.approach.map((paragraph, i) => (
                <p key={i} className="text-lg leading-relaxed text-ink/85">
                  {paragraph}
                </p>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <img
              src={ASSETS.safetyApproach.src}
              alt={ASSETS.safetyApproach.alt}
              loading="lazy"
              style={{ objectPosition: ASSETS.safetyApproach.position }}
              className="aspect-[4/3] w-full rounded-2xl object-cover lg:aspect-auto lg:h-full"
            />
          </Reveal>
        </div>
      </section>

      <CTASection
        heading="Ready to step out of the classroom?"
        primary={{ label: "Explore Our Journeys", to: "/journeys", testId: "approach-journeys-cta" }}
        secondary={{ label: "Start Your Journey", to: "/start-your-journey", testId: "approach-start-cta" }}
        testId="approach-cta"
      />
    </>
  );
}
